import { contrastingText, contrastRatio, mixColors, readableColor } from './color-contrast';

export interface TenantBranding {
  tenantId: string;
  primaryColor: string;
  secondaryColor?: string;
  logoUrl?: string;
  brandName?: string;
  updatedAt: string;
}

const STORAGE_PREFIX = 'kontora_tenant_branding_v1';
export const DEFAULT_PRIMARY = '#1e5b8c';

export const PRESET_COLORS: Array<{ name: string; value: string }> = [
  { name: 'Kontora-Blau', value: '#1e5b8c' },
  { name: 'Türkis', value: '#0f766e' },
  { name: 'Grün', value: '#15803d' },
  { name: 'Orange', value: '#c2410c' },
  { name: 'Violett', value: '#7c3aed' },
  { name: 'Pink', value: '#be185d' },
  { name: 'Indigo', value: '#4338ca' },
  { name: 'Anthrazit', value: '#374151' },
];

function storageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}__${tenantId}`;
}

function defaultBranding(tenantId: string): TenantBranding {
  return {
    tenantId,
    primaryColor: DEFAULT_PRIMARY,
    updatedAt: new Date(0).toISOString(),
  };
}

export function loadBranding(tenantId: string): TenantBranding {
  if (typeof window === 'undefined') {
    return defaultBranding(tenantId);
  }
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) {
      return defaultBranding(tenantId);
    }
    const parsed = JSON.parse(raw) as Partial<TenantBranding> | null;
    if (!parsed || parsed.tenantId !== tenantId || typeof parsed.primaryColor !== 'string' || !isValidHexColor(parsed.primaryColor)) {
      return defaultBranding(tenantId);
    }
    return {
      ...parsed,
      tenantId,
      primaryColor: parsed.primaryColor,
      secondaryColor: typeof parsed.secondaryColor === 'string' && isValidHexColor(parsed.secondaryColor) ? parsed.secondaryColor : undefined,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date(0).toISOString(),
    };
  } catch {
    return defaultBranding(tenantId);
  }
}

export function saveBranding(branding: TenantBranding): TenantBranding {
  const updated: TenantBranding = { ...branding, updatedAt: new Date().toISOString() };
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(storageKey(branding.tenantId), JSON.stringify(updated));
    } catch (err) {
      const key = storageKey(branding.tenantId);
      console.warn('[tenant-branding] saveBranding() fehlgeschlagen für Schlüssel "' + key + '" (tenantId="' + branding.tenantId + '"). Branding werden auf Default zurückgesetzt.', err instanceof Error ? err.message : String(err));
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kontora-branding-change', { detail: branding.tenantId }));
  }
  return updated;
}

export function applyBranding(branding: TenantBranding): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const properties = ['accent', 'accent-soft', 'accent-strong', 'accent-stronger', 'text-link', 'text-on-accent'];
  if (!isValidHexColor(branding.primaryColor) || (branding.primaryColor.toLowerCase() === DEFAULT_PRIMARY && !branding.secondaryColor)) {
    for (const property of properties) root.style.removeProperty(`--k-color-${property}`);
    return;
  }
  const dark = root.getAttribute('data-theme') === 'dark';
  const accent = readableColor(branding.primaryColor, '#eef2f7', '#000000');
  const link = readableColor(branding.primaryColor, dark ? '#1f2a42' : '#eef2f7', dark ? '#ffffff' : '#000000');
  const generatedSoft = mixColors(accent, dark ? '#131b2e' : '#ffffff', dark ? 0.8 : 0.9);
  const secondary = branding.secondaryColor;
  const soft = secondary && isValidHexColor(secondary) && contrastRatio(link, secondary) >= 4.5 && contrastRatio(dark ? '#94a3b8' : '#5a6b7e', secondary) >= 4.5 ? secondary : generatedSoft;
  root.style.setProperty('--k-color-accent', accent);
  root.style.setProperty('--k-color-accent-soft', soft);
  root.style.setProperty('--k-color-accent-strong', mixColors(accent, '#000000', 0.18));
  root.style.setProperty('--k-color-accent-stronger', mixColors(accent, '#000000', 0.3));
  root.style.setProperty('--k-color-text-link', link);
  root.style.setProperty('--k-color-text-on-accent', contrastingText(accent));
}

export function isValidHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}){1,2}$/.test(value);
}

function toHex(value: number): string {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
}

export function generateVariants(primary: string): { soft: string; strong: string } {
  if (!isValidHexColor(primary)) return { soft: '#d9e8f5', strong: primary };
  const expanded = primary.length === 4
    ? `#${primary[1]}${primary[1]}${primary[2]}${primary[2]}${primary[3]}${primary[3]}`
    : primary;
  const r = parseInt(expanded.slice(1, 3), 16);
  const g = parseInt(expanded.slice(3, 5), 16);
  const b = parseInt(expanded.slice(5, 7), 16);
  const channels = [r, g, b];
  const soft = `#${channels.map((c) => toHex(c + (255 - c) * 0.85)).join('')}`;
  const strong = `#${channels.map((c) => toHex(c * 0.78)).join('')}`;
  return { soft, strong };
}
